import { Card, Divider, Flex, Form, Input, Skeleton as AntdComponents, Spin } from "antd";
import { ItemInfo } from "components";
import { ConfirmOtpModal } from "components/ConfirmOtpModal";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { NOMINAL_ACCOUNT_REQUESTS_DELAY } from "constants/nominalAccount";
import { CompanyEntity, OPERATION_TYPE } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useEffect, useState } from "react";
import { useStores } from "stores";
import { CompanySelect, CustomButton, DefaultInput, NumberInput, Skeleton } from "ui";
import {
  _isRequired,
  _greaterOrEqual,
  _isNotLongerThan,
  requestWithNotify,
  formatWithDecimals,
  RUB_CURRENCY,
} from "utils";

export const ApproveDepositModal = observer(() => {
  const { actionModals, rubOperations } = useStores();
  const { openNotification } = useNotificationContext();
  const [selectedCompany, setSelectedCompany] = useState<CompanyEntity | null>(null);

  const [form] = Form.useForm();
  const amountValue = Number(Form.useWatch("amount", form));
  const selectCompanyValue = Form.useWatch("companyId", form);

  const disabled = amountValue === 0 || !selectCompanyValue;
  const tooltip = !selectCompanyValue ? "Не выбрана компания" : amountValue === 0 ? "Введите сумму" : undefined;

  function showOtpModal() {
    const values = form.getFieldsValue();

    actionModals.open(
      () => (
        <ConfirmOtpModal
          operationPath={OPERATION_TYPE.NOMINAL_ACCOUNT_DEPOSIT}
          requestBody={{ ...values, amount: amountValue.toFixed(2), companyId: selectedCompany?.id }}
          notificationMsg={{
            successMsg: "Депозит зачислен",
          }}
          description={
            <Flex vertical>
              <Divider />
              <ItemInfo title="Название компании" text={selectedCompany?.companyName} vertical={false} />
              <ItemInfo
                title="Сумма"
                text={`${formatWithDecimals(amountValue)} ${RUB_CURRENCY.RUB}`}
                vertical={false}
              />
              {values["proofOfPayment"] && (
                <ItemInfo title="Номер платежного поручения" text={values["proofOfPayment"]} vertical={false} />
              )}
              <Divider className="margin-top-none" />
            </Flex>
          }
          onSuccess={() => handleSuccess()}
          cancelBtnProps={{
            position: "bottom",
            title: "Вернуться назад",
            closeHandler: () => {
              actionModals.closeLastModal();
            },
          }}
        />
      ),
      {
        title: "Подтвердите депозит",
        width: "620px",
        onCloseHeader: () => actionModals.closeAll(),
      }
    );
  }

  function handleSuccess() {
    handleCloseModal();
    form.resetFields();
    setSelectedCompany(null);
    requestWithNotify({
      fetchFn: () => rubOperations.fetch(),
      openNotification,
      delay: NOMINAL_ACCOUNT_REQUESTS_DELAY,
    });
  }

  function handleCloseModal() {
    actionModals.closeAll();
  }

  useEffect(() => {
    form.setFieldsValue({
      amount: "",
    });
  }, []);

  return (
    <Flex vertical>
      <Divider className="margin-top-none" />
      <Form form={form}>
        <CompanySelect
          inputProps={{
            disabled: false,
            placeholder: "Название компании",
          }}
          name="companyId"
          onDataReceived={(data) => setSelectedCompany(data)}
        />
        <Form.Item
          name="amount"
          className="full-width"
          validateDebounce={FORM_VALIDATION_DEBOUNCE}
          rules={[_isRequired("Поле обязательно для ввода"), _greaterOrEqual(0)]}
        >
          <NumberInput placeholder="Сумма" />
        </Form.Item>
        <Form.Item
          name="proofOfPayment"
          className="full-width"
          validateDebounce={FORM_VALIDATION_DEBOUNCE}
          rules={[_greaterOrEqual(0), _isNotLongerThan(30)]}
        >
          <DefaultInput placeholder="Номер платежного поручения" />
        </Form.Item>
        <Form.Item>
          <Flex gap="large">
            <CustomButton fullWidth onClick={handleCloseModal} variant={"outlined"} style={{ width: "240px" }}>
              Отмена
            </CustomButton>
            <CustomButton
              fullWidth
              onClick={showOtpModal}
              disabled={disabled}
              tooltip={tooltip}
              style={{ width: "240px" }}
            >
              Подтвердить
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
});
